import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { compareCzech, type GoogleReviewAdminItem } from "@ckgo2/shared";
import { api, type ApiResponse } from "@/api/client";

export interface DestinationOption {
  id: string;
  label: string;
}

interface SlimDestination {
  id: string;
  name: string;
  parentId: string | null;
}

/**
 * Volby destinací pro Google recenze ze slim projekce veřejného seznamu (jen publikované,
 * tedy přesně množina, ze které přiřazuje F2.7). Plný `/api/destinations` má v produkci přes
 * 14 MB; slim ~0,24 MB. Seznam, editor i dialog sdílejí jeden dotaz, stáhne se jednou.
 *
 * Jména nejsou unikátní (v produkci dvakrát „Victoria Peak"), proto místo pod rodičem
 * nese v popisku i jméno rodiče. Řadí se `compareCzech` (jedno české pořadí bez ohledu
 * na jazyk UI).
 */
export function useDestinationOptions(): DestinationOption[] {
  const { data } = useQuery({
    queryKey: ["destinations", "slim"],
    queryFn: () => api.get<ApiResponse<SlimDestination[]>>("/api/destinations?fields=slim"),
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const rows = data?.data ?? [];
    const nameById = new Map(rows.map((d) => [d.id, d.name]));
    return rows
      .map((d) => {
        const parent = d.parentId ? nameById.get(d.parentId) : undefined;
        return { id: d.id, label: parent ? `${d.name} (${parent})` : d.name };
      })
      .sort((a, b) => compareCzech(a.label, b.label));
  }, [data]);
}

/**
 * Když má recenze destinaci, která ve slim seznamu není (mezitím nepublikovaná), přidá ji
 * jako volbu, aby `<select>` uloženou hodnotu nezahodil a neukázal jinou.
 */
export function withCurrent(
  options: DestinationOption[],
  item: Pick<GoogleReviewAdminItem, "destinationId" | "destinationName"> | null | undefined,
): DestinationOption[] {
  if (!item?.destinationId || options.some((o) => o.id === item.destinationId)) return options;
  return [
    { id: item.destinationId, label: item.destinationName ?? item.destinationId },
    ...options,
  ];
}
